import type { AppState, RenderBackend } from '../state/appState.js';
import type { ParamValue } from '../graph/nodeDefs.js';
import { isContourOverrideArray } from '../graph/paramKeys.js';
import { waitForWebGPUIdle } from '../rendering/webgpuRenderer.js';
import type { ArcCoverageMode } from '../sdf/arcCoverageMode.js';
import { resolveArcCoverageMode } from '../sdf/arcCoverageMode.js';
import type { RenderLoopController } from './renderLoop.js';
import {
  deserializeFile,
  restoreFile,
  serializeFile,
} from './file.js';
import { importFileLabel } from '../editor/historyLabels.js';
import type { CredentialResolver, DataProviderRegistry, DataSourceProvider, MemoryDataProvider, MemoryDataSource } from './dataProviders.js';
import type { DataSourceCredentialStore } from './dataSourceCredentials.js';
import type { DataSourceRuntimeManager } from './dataSourceRuntime.js';

interface DebugApiDeps {
  appState: AppState;
  renderLoop: RenderLoopController;
  syncViewportControls: () => void;
  syncEditor: () => void;
  resetPan: () => void;
  resetHistory: (label?: string) => void;
  renderGraph: () => void;
  updateParams: () => void;
  renderSceneTree: () => void;
  renderEditorNavigation: () => void;
  memoryDataProvider?: MemoryDataProvider;
  dataSourceCredentials?: DataSourceCredentialStore;
  dataSourceRuntime?: DataSourceRuntimeManager;
  dataProviderRegistry?: DataProviderRegistry;
}

interface BenchmarkOptions {
  frames?: number;
  warmup?: number;
  modes?: ArcCoverageMode[];
  resetWebGPUStateEachMode?: boolean;
}

interface RenderSample {
  mode: ArcCoverageMode;
  frame: number;
  elapsedMs: number;
}

interface BenchmarkModeResult {
  mode: ArcCoverageMode;
  frames: number;
  minMs: number;
  medianMs: number;
  meanMs: number;
  maxMs: number;
  samples: number[];
}

interface BenchmarkResult {
  backend: RenderBackend;
  resolution: { w: number; mode: string };
  warmup: number;
  results: BenchmarkModeResult[];
  samples: RenderSample[];
}

interface BenchmarkColorParamOptions extends BenchmarkOptions {
  paramKey: string;
  baseValue?: ParamValue;
  component?: number;
  pulse?: number;
  restoreOriginal?: boolean;
}

interface NumericStats {
  min: number;
  median: number;
  p90: number;
  max: number;
  mean: number;
}

interface ResourceCounterSample {
  count: number;
  timeMs: number;
  bytes?: number;
  large?: number;
}

type ResourceCounterName = 'createBuffer' | 'writeBuffer' | 'createBindGroup';
type ResourceCounters = Record<ResourceCounterName, ResourceCounterSample>;

interface ResourceCounterStats {
  count: NumericStats;
  timeMs: NumericStats;
  bytes?: NumericStats;
  large?: NumericStats;
}

interface ColorParamRenderSample {
  mode: ArcCoverageMode;
  frame: number;
  elapsedMs: number;
  counters: ResourceCounters;
}

interface BenchmarkColorParamModeResult {
  mode: ArcCoverageMode;
  frames: number;
  elapsedMs: NumericStats;
  counters: Record<ResourceCounterName, ResourceCounterStats>;
}

interface BenchmarkColorParamResult {
  backend: RenderBackend;
  paramKey: string;
  baseValue: ParamValue;
  component: number;
  pulse: number;
  warmup: number;
  results: BenchmarkColorParamModeResult[];
  samples: ColorParamRenderSample[];
}

export interface FileLoadResult {
  documentId: string;
  sourceName?: string;
  rootCount: number;
  componentCount: number;
  layerCount: number;
}

export interface FluveriaDebugApi {
  state: AppState;
  setBackend(backend: RenderBackend): void;
  setCoverageMode(mode: ArcCoverageMode): void;
  setResolution(resolution: number): void;
  exportFile(): string;
  loadFileText(data: string, sourceName?: string): FileLoadResult;
  loadFileJson(file: unknown, sourceName?: string): FileLoadResult;
  loadFile(file?: File): Promise<FileLoadResult | null>;
  renderNow(): Promise<number>;
  benchmarkRender(options?: BenchmarkOptions): Promise<BenchmarkResult>;
  benchmarkColorParamRender(options: BenchmarkColorParamOptions): Promise<BenchmarkColorParamResult>;
  registerMemoryDataSource(resource: string, source: MemoryDataSource): () => void;
  registerDataProvider(kind: string, provider: DataSourceProvider): () => void;
  setWebSocketBearerCredential(reference: string, token: string, allowedOrigins: readonly string[]): void;
  clearCredential(reference: string): void;
  setCredentialResolver(resolver: CredentialResolver): void;
  refreshDataSources(): Promise<void>;
}

declare global {
  interface Window {
    fluveria?: FluveriaDebugApi;
  }
}

function pickFile(): Promise<File | null> {
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.fluveria,.json,application/json';
    input.style.display = 'none';
    input.addEventListener('change', () => {
      const file = input.files?.[0] ?? null;
      input.remove();
      resolve(file);
    }, { once: true });
    document.body.appendChild(input);
    input.click();
  });
}

function percentile(sorted: number[], q: number): number {
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const next = sorted[base + 1];
  if (next === undefined) return sorted[base];
  return sorted[base] + (pos - base) * (next - sorted[base]);
}

function numericStats(values: number[]): NumericStats {
  const sorted = [...values].sort((a, b) => a - b);
  return {
    min: sorted[0],
    median: percentile(sorted, 0.5),
    p90: percentile(sorted, 0.9),
    max: sorted[sorted.length - 1],
    mean: values.reduce((sum, value) => sum + value, 0) / values.length,
  };
}

function createResourceCounters(): ResourceCounters {
  return {
    createBuffer: { count: 0, timeMs: 0, bytes: 0, large: 0 },
    writeBuffer: { count: 0, timeMs: 0, bytes: 0 },
    createBindGroup: { count: 0, timeMs: 0 },
  };
}

function counterStats(samples: ColorParamRenderSample[], name: ResourceCounterName): ResourceCounterStats {
  const first = samples[0].counters[name];
  return {
    count: numericStats(samples.map(sample => sample.counters[name].count)),
    timeMs: numericStats(samples.map(sample => sample.counters[name].timeMs)),
    bytes: first.bytes === undefined ? undefined : numericStats(samples.map(sample => sample.counters[name].bytes ?? 0)),
    large: first.large === undefined ? undefined : numericStats(samples.map(sample => sample.counters[name].large ?? 0)),
  };
}

/** Colour benchmark params are numeric (scalar or vector); contour overrides are not pulsable. */
type NumericParamValue = number | number[] | string;

function numericParam(value: ParamValue, key: string): NumericParamValue {
  if (isContourOverrideArray(value)) throw new Error(`Benchmark param is a contours override: ${key}`);
  return value as NumericParamValue;
}

function readBenchmarkBaseValue(params: AppState['params'], options: BenchmarkColorParamOptions): NumericParamValue {
  const current = params[options.paramKey];
  if (current !== undefined) {
    const value = numericParam(current, options.paramKey);
    return Array.isArray(value) ? [...value] : value;
  }
  if (options.baseValue !== undefined) {
    const value = numericParam(options.baseValue, options.paramKey);
    return Array.isArray(value) ? [...value] : value;
  }
  throw new Error(`Missing benchmark param: ${options.paramKey}`);
}

function pulseParamValue(baseValue: NumericParamValue, component: number, pulse: number): NumericParamValue {
  if (Array.isArray(baseValue)) {
    const next = [...baseValue];
    const index = Math.min(Math.max(0, component), Math.max(0, next.length - 1));
    next[index] = (Number(next[index]) || 0) + pulse;
    return next;
  }
  return (Number(baseValue) || 0) + pulse;
}

function installGpuResourceCounterPatch(getActiveCounters: () => ResourceCounters | null): () => void {
  const globalGpuDevice = globalThis as typeof globalThis & {
    GPUDevice?: { prototype: GPUDevice };
    GPUQueue?: { prototype: GPUQueue };
  };
  const devicePrototype = globalGpuDevice.GPUDevice?.prototype;
  const queuePrototype = globalGpuDevice.GPUQueue?.prototype;
  const restore: Array<() => void> = [];

  if (devicePrototype?.createBuffer) {
    const original = devicePrototype.createBuffer;
    devicePrototype.createBuffer = function patchedCreateBuffer(this: GPUDevice, descriptor: GPUBufferDescriptor): GPUBuffer {
      const counters = getActiveCounters();
      if (!counters) return original.call(this, descriptor);
      const t0 = performance.now();
      const result = original.call(this, descriptor);
      const elapsed = performance.now() - t0;
      const bytes = Number(descriptor.size ?? 0);
      counters.createBuffer.count += 1;
      counters.createBuffer.timeMs += elapsed;
      counters.createBuffer.bytes = (counters.createBuffer.bytes ?? 0) + bytes;
      counters.createBuffer.large = (counters.createBuffer.large ?? 0) + (bytes > 1024 ? 1 : 0);
      return result;
    };
    restore.push(() => { devicePrototype.createBuffer = original; });
  }

  if (devicePrototype?.createBindGroup) {
    const original = devicePrototype.createBindGroup;
    devicePrototype.createBindGroup = function patchedCreateBindGroup(this: GPUDevice, descriptor: GPUBindGroupDescriptor): GPUBindGroup {
      const counters = getActiveCounters();
      if (!counters) return original.call(this, descriptor);
      const t0 = performance.now();
      const result = original.call(this, descriptor);
      counters.createBindGroup.count += 1;
      counters.createBindGroup.timeMs += performance.now() - t0;
      return result;
    };
    restore.push(() => { devicePrototype.createBindGroup = original; });
  }

  if (queuePrototype?.writeBuffer) {
    const original = queuePrototype.writeBuffer;
    queuePrototype.writeBuffer = function patchedWriteBuffer(
      this: GPUQueue,
      buffer: GPUBuffer,
      bufferOffset: GPUSize64,
      data: BufferSource,
      dataOffset?: GPUSize64,
      size?: GPUSize64,
    ): undefined {
      const counters = getActiveCounters();
      if (!counters) return original.call(this, buffer, bufferOffset, data, dataOffset, size);
      const t0 = performance.now();
      const result = original.call(this, buffer, bufferOffset, data, dataOffset, size);
      counters.writeBuffer.count += 1;
      counters.writeBuffer.timeMs += performance.now() - t0;
      counters.writeBuffer.bytes = (counters.writeBuffer.bytes ?? 0) + Number(size ?? data.byteLength ?? 0);
      return result;
    };
    restore.push(() => { queuePrototype.writeBuffer = original; });
  }

  return () => {
    for (const restorePatch of restore.reverse()) restorePatch();
  };
}

export function installDebugApi(deps: DebugApiDeps): void {
  const { appState, renderLoop, syncViewportControls } = deps;

  const renderNow = async (): Promise<number> => {
    const t0 = performance.now();
    await renderLoop.renderNow();
    if (appState.backend === 'webgpu') await waitForWebGPUIdle();
    return performance.now() - t0;
  };

  const fileLoadResult = (sourceName?: string): FileLoadResult => ({
    documentId: appState.documentId,
    sourceName,
    rootCount: appState.scene.rootIds.length,
    componentCount: Object.keys(appState.componentLibrary).length,
    layerCount: Object.keys(appState.layerLibrary).length,
  });

  const refreshFileAfterLoad = (sourceName?: string): FileLoadResult => {
    deps.resetPan();
    deps.resetHistory(importFileLabel());
    deps.renderGraph();
    deps.updateParams();
    deps.renderSceneTree();
    deps.renderEditorNavigation();
    syncViewportControls();
    renderLoop.resetRenderState();
    renderLoop.scheduleRender();
    return fileLoadResult(sourceName);
  };

  window.fluveria = {
    state: appState,
    setBackend(backend): void {
      appState.backend = backend;
      renderLoop.resetRenderState();
      syncViewportControls();
      renderLoop.scheduleRender();
    },
    setCoverageMode(mode): void {
      appState.arcCoverageMode = resolveArcCoverageMode(mode);
      renderLoop.resetRenderState();
      syncViewportControls();
      renderLoop.scheduleRender();
    },
    setResolution(resolution): void {
      appState.resolution = Math.max(1, Math.round(Number(resolution) || appState.resolution));
      appState.viewportSizeMode = 'fixed';
      renderLoop.resetRenderState();
      syncViewportControls();
      renderLoop.scheduleRender();
    },
    exportFile(): string {
      deps.syncEditor();
      return serializeFile(appState);
    },
    loadFileText(data, sourceName): FileLoadResult {
      deserializeFile(appState, data);
      return refreshFileAfterLoad(sourceName);
    },
    loadFileJson(file, sourceName): FileLoadResult {
      restoreFile(appState, file);
      return refreshFileAfterLoad(sourceName);
    },
    async loadFile(file): Promise<FileLoadResult | null> {
      const selectedFile = file ?? await pickFile();
      if (!selectedFile) return null;
      return this.loadFileText(await selectedFile.text(), selectedFile.name);
    },
    renderNow,
    registerMemoryDataSource(resource, source): () => void {
      if (!deps.memoryDataProvider) throw new Error('Memory data provider is unavailable');
      return deps.memoryDataProvider.register(resource, source);
    },
    registerDataProvider(kind, provider): () => void {
      if (!deps.dataProviderRegistry) throw new Error('Data provider registry is unavailable');
      return deps.dataProviderRegistry.register(kind, provider);
    },
    setWebSocketBearerCredential(reference, token, allowedOrigins): void {
      if (!deps.dataSourceCredentials) throw new Error('Data source credential store is unavailable');
      deps.dataSourceCredentials.setWebSocketBearer(reference, token, allowedOrigins);
    },
    clearCredential(reference): void {
      if (!deps.dataSourceCredentials) throw new Error('Data source credential store is unavailable');
      deps.dataSourceCredentials.clear(reference);
    },
    setCredentialResolver(resolver): void {
      if (!deps.dataSourceRuntime) throw new Error('Data source runtime is unavailable');
      deps.dataSourceRuntime.setCredentialResolver(resolver);
    },
    async refreshDataSources(): Promise<void> {
      if (!deps.dataSourceRuntime) throw new Error('Data source runtime is unavailable');
      await deps.dataSourceRuntime.connectAll();
    },
    async benchmarkRender(options = {}): Promise<BenchmarkResult> {
      const frames = Math.max(1, Math.round(options.frames ?? 60));
      const warmup = Math.max(0, Math.round(options.warmup ?? 10));
      const modes = options.modes?.length ? options.modes.map(resolveArcCoverageMode) : [appState.arcCoverageMode];
      const samples: RenderSample[] = [];
      const results: BenchmarkModeResult[] = [];

      for (const mode of modes) {
        appState.arcCoverageMode = mode;
        if (options.resetWebGPUStateEachMode ?? true) renderLoop.resetRenderState();
        syncViewportControls();
        for (let i = 0; i < warmup; i++) {
          await renderNow();
        }

        const modeSamples: number[] = [];
        for (let frame = 0; frame < frames; frame++) {
          const elapsedMs = await renderNow();
          modeSamples.push(elapsedMs);
          samples.push({ mode, frame, elapsedMs });
        }
        const sorted = [...modeSamples].sort((a, b) => a - b);
        results.push({
          mode,
          frames,
          minMs: sorted[0],
          medianMs: sorted[Math.floor(sorted.length / 2)],
          meanMs: modeSamples.reduce((sum, value) => sum + value, 0) / modeSamples.length,
          maxMs: sorted[sorted.length - 1],
          samples: modeSamples,
        });
      }

      return {
        backend: appState.backend,
        resolution: { w: appState.resolution, mode: appState.viewportSizeMode },
        warmup,
        results,
        samples,
      };
    },
    async benchmarkColorParamRender(options): Promise<BenchmarkColorParamResult> {
      const frames = Math.max(1, Math.round(options.frames ?? 60));
      const warmup = Math.max(0, Math.round(options.warmup ?? 10));
      const component = Math.max(0, Math.round(options.component ?? 0));
      const pulse = Number.isFinite(options.pulse) ? Number(options.pulse) : 0.012;
      const restoreOriginal = options.restoreOriginal ?? true;
      const modes = options.modes?.length ? options.modes.map(resolveArcCoverageMode) : [appState.arcCoverageMode];
      const baseValue = readBenchmarkBaseValue(appState.params, options);
      const originalValue = appState.params[options.paramKey];
      const samples: ColorParamRenderSample[] = [];
      const results: BenchmarkColorParamModeResult[] = [];
      let activeCounters: ResourceCounters | null = null;
      const restoreGpuPatch = installGpuResourceCounterPatch(() => activeCounters);

      const renderMeasuredFrame = async (mode: ArcCoverageMode, frame: number): Promise<ColorParamRenderSample> => {
        const counters = createResourceCounters();
        activeCounters = counters;
        try {
          return {
            mode,
            frame,
            elapsedMs: await renderNow(),
            counters,
          };
        } finally {
          activeCounters = null;
        }
      };

      try {
        for (const mode of modes) {
          appState.arcCoverageMode = mode;
          if (options.resetWebGPUStateEachMode ?? true) renderLoop.resetRenderState();
          syncViewportControls();
          for (let i = 0; i < warmup; i++) {
            appState.params[options.paramKey] = pulseParamValue(baseValue, component, i % 2 === 0 ? pulse : -pulse);
            await renderNow();
          }

          const modeSamples: ColorParamRenderSample[] = [];
          for (let frame = 0; frame < frames; frame++) {
            appState.params[options.paramKey] = pulseParamValue(baseValue, component, frame % 2 === 0 ? pulse : -pulse);
            const sample = await renderMeasuredFrame(mode, frame);
            modeSamples.push(sample);
            samples.push(sample);
          }

          results.push({
            mode,
            frames,
            elapsedMs: numericStats(modeSamples.map(sample => sample.elapsedMs)),
            counters: {
              createBuffer: counterStats(modeSamples, 'createBuffer'),
              writeBuffer: counterStats(modeSamples, 'writeBuffer'),
              createBindGroup: counterStats(modeSamples, 'createBindGroup'),
            },
          });
        }
      } finally {
        restoreGpuPatch();
        if (restoreOriginal) {
          if (originalValue === undefined) {
            delete appState.params[options.paramKey];
          } else {
            appState.params[options.paramKey] = originalValue;
          }
        }
      }

      return {
        backend: appState.backend,
        paramKey: options.paramKey,
        baseValue,
        component,
        pulse,
        warmup,
        results,
        samples,
      };
    },
  };
}
